import React, { useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { WidgetEmployeeSchedulePreview } from "./WidgetEmployeeSchedulePreview";
import { WidgetEmployeeScheduleSettings } from "./WidgetEmployeeScheduleSettings";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WidgetScreen } from "../../common/WidgetScreen";

import { WSEmployeeScheduleQuery } from "./__generated__/WSEmployeeScheduleQuery.graphql";
import { WSEmployeeScheduleMutation } from "./__generated__/WSEmployeeScheduleMutation.graphql";
import { useWidgetEmployeeSchedulePreviewQueryLoader } from "./useWidgetEmployeeSchedulePreviewQueryLoader";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { WidgetScreenPreview } from "../WidgetScreenPreview/WidgetScreenPreview";

type Props = UserLoggedInNavigatorScreenProps<"WSEmployeeSchedule">;

export const WSEmployeeSchedule = withWebModal(WSEmployeeScheduleC, true);
function WSEmployeeScheduleC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerWSEmployeeSchedule {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

const WS_EMPLOYEE_SCHEDULE_QUERY = graphql`
  query WSEmployeeScheduleQuery($id: ID!) {
    widgetEmployeeSchedule(id: $id) {
      id
      teams {
        id
      }
    }
    store {
      ...WidgetEmployeeScheduleSettingsQuery_store
    }
  }
`;

function InnerWSEmployeeSchedule(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<WSEmployeeScheduleQuery>(
    WS_EMPLOYEE_SCHEDULE_QUERY,
    {
      id: props.route.params.widgetId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [teamIds, setTeamIds] = useState<string[]>(
    data.widgetEmployeeSchedule?.teams.map(({ id }) => id) ?? []
  );

  const query = useWidgetEmployeeSchedulePreviewQueryLoader({
    storecastId: props.route.params.storecastId,
    teamIds,
  });

  const [saveWidgetEmployeeSchedule, loading] =
    useMutation<WSEmployeeScheduleMutation>(graphql`
      mutation WSEmployeeScheduleMutation(
        $storecastId: ID!
        $widgetId: ID!
        $input: UpdateWidgetEmployeeScheduleInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          updateWidgetEmployeeSchedule(input: $input, widgetId: $widgetId) {
            id
            teams {
              id
            }
          }
        }
      }
    `);

  const onSave = useCallback(() => {
    saveWidgetEmployeeSchedule({
      variables: {
        storecastId: props.route.params.storecastId,
        widgetId: props.route.params.widgetId,
        input: {
          setTeamIds: teamIds,
        },
      },
      onCompleted: props.navigation.goBack,
      onError: (e) => {
        console.log("e", e);
      },
    });
  }, [
    props.navigation,
    props.route.params.storecastId,
    props.route.params.widgetId,
    saveWidgetEmployeeSchedule,
    teamIds,
  ]);

  return (
    <WidgetScreen
      preview={
        <WidgetScreenPreview
          onSave={onSave}
          loading={loading}
          unitSize={props.route.params.unitSize}
          size={props.route.params.size}
        >
          {query ? (
            <WidgetEmployeeSchedulePreview
              storecastId={props.route.params.storecastId}
              query={query}
            />
          ) : null}
        </WidgetScreenPreview>
      }
      settings={
        <WidgetEmployeeScheduleSettings
          store={data.store}
          teamIds={teamIds}
          setTeamIds={setTeamIds}
        />
      }
    />
  );
}
